// Hideo - a folder of the computer seen as a FolderHandle, for the desktop app's data folder (src/features/desktop.ts):
// the part of the File System Access API that folder.ts and the profile picker use (getFileHandle, getDirectoryHandle,
// removeEntry, queryPermission), over Tauri's fs plugin. A file is written to "<name>.tmp" and then renamed, so a crash
// in the middle never leaves a half-written profile.
import type { FolderHandle } from './folder'

const fs = () => import('@tauri-apps/plugin-fs')
/** dir + name with the separator dir already uses (Windows paths have "\") */
export const joinPath = (dir: string, name: string): string => {
  const sep = dir.includes('\\') && !dir.includes('/') ? '\\' : '/'
  return dir.replace(/[\\/]+$/, '') + sep + name
}
const notFound = (name: string) => new DOMException(`${name} not found`, 'NotFoundError')

function nativeFile(path: string, name: string) {
  return {
    kind: 'file' as const, name,
    getFile: async () => { const text = await (await fs()).readTextFile(path); return { text: async () => text } },
    createWritable: async () => {
      let buf = ''
      return {
        write: async (s: string) => { buf += s },
        close: async () => { const f = await fs(); await f.writeTextFile(path + '.tmp', buf); await f.rename(path + '.tmp', path) }
      }
    }
  }
}

/** The folder at this path, as a FolderHandle (the permission is the desktop app's: always granted) */
export function nativeFolder(path: string): FolderHandle {
  const handle = {
    kind: 'directory' as const, name: path,
    queryPermission: async () => 'granted',
    async getFileHandle(name: string, o?: { create?: boolean }) {
      const p = joinPath(path, name)
      if (!o?.create && !(await (await fs()).exists(p))) throw notFound(name)
      return nativeFile(p, name)
    },
    async getDirectoryHandle(name: string, o?: { create?: boolean }) {
      const p = joinPath(path, name), f = await fs()
      if (o?.create) await f.mkdir(p, { recursive: true }); else if (!(await f.exists(p))) throw notFound(name)
      return nativeFolder(p)
    },
    async removeEntry(name: string) { await (await fs()).remove(joinPath(path, name)) }
  }
  return handle as unknown as FolderHandle
}
